import { getFunctions, httpsCallable } from 'firebase/functions';

interface RetryConfig {
  maxRetries: number;
  retryInterval: number; // in milliseconds
  backoffMultiplier: number;
}

interface RetryTask {
  postcardId: string;
  exchangeId: string;
  retryCount: number;
  maxRetries: number;
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
  onComplete?: (result: any) => void;
}

class RetryService {
  private retryQueue: RetryTask[] = [];
  private isProcessing = false;
  private config: RetryConfig = {
    maxRetries: 5,
    retryInterval: 30000, // 30 seconds
    backoffMultiplier: 1.5
  };

  constructor() {
    // Start processing when service is created
    this.processQueue();
  }

  // Add a postcard to retry queue
  addToRetryQueue(task: Omit<RetryTask, 'retryCount'>): void {
    const retryTask: RetryTask = {
      ...task,
      retryCount: 0
    };
    
    this.retryQueue.push(retryTask);
    console.log(`Added postcard ${task.postcardId} to retry queue`);
    
    // Start processing if not already running
    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  // Process the retry queue in background
  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.retryQueue.length === 0) {
      return;
    }

    this.isProcessing = true;
    console.log(`Processing ${this.retryQueue.length} retry tasks`);

    while (this.retryQueue.length > 0) {
      const task = this.retryQueue.shift();
      if (!task) continue;

      try {
        await this.processRetryTask(task);
      } catch (error) {
        console.error(`Error processing retry task for postcard ${task.postcardId}:`, error);
        task.onError?.(error);
      }
    }

    this.isProcessing = false;
    console.log('Retry queue processing completed');
  }

  // Process a single retry task
  private async processRetryTask(task: RetryTask): Promise<void> {
    console.log(`Processing retry ${task.retryCount + 1} for postcard ${task.postcardId}`);

    try {
      const functions = getFunctions();
      const retryFailedPostcard = httpsCallable(functions, 'retryFailedPostcard');
      
      const result = await retryFailedPostcard({
        postcardId: task.postcardId,
        exchangeId: task.exchangeId
      });

      const data = result.data as any;

      if (data.success) {
        // Success! Postcard was sent
        console.log(`Successfully sent postcard ${task.postcardId} to ${data.recipientName}`);
        task.onSuccess?.(data);
        task.onComplete?.(data);
      } else if (data.shouldRetry && task.retryCount < task.maxRetries - 1) {
        // Should retry again
        const nextRetryCount = task.retryCount + 1;
        const waitTime = this.config.retryInterval * Math.pow(this.config.backoffMultiplier, nextRetryCount);
        
        console.log(`Scheduling retry ${nextRetryCount} for postcard ${task.postcardId} in ${waitTime}ms`);
        
        // Schedule next retry
        setTimeout(() => {
          const nextTask: RetryTask = {
            ...task,
            retryCount: nextRetryCount
          };
          this.retryQueue.push(nextTask);
          this.processQueue();
        }, waitTime);
      } else {
        // Max retries reached or should not retry
        console.log(`Max retries reached for postcard ${task.postcardId}`);
        task.onError?.(new Error(data.message));
        task.onComplete?.({ success: false, message: data.message });
      }
    } catch (error) {
      console.error(`Error in retry attempt ${task.retryCount + 1} for postcard ${task.postcardId}:`, error);
      
      if (task.retryCount < task.maxRetries - 1) {
        // Schedule retry on error
        const nextRetryCount = task.retryCount + 1;
        const waitTime = this.config.retryInterval * Math.pow(this.config.backoffMultiplier, nextRetryCount);
        
        setTimeout(() => {
          const nextTask: RetryTask = {
            ...task,
            retryCount: nextRetryCount
          };
          this.retryQueue.push(nextTask);
          this.processQueue();
        }, waitTime);
      } else {
        // Max retries reached
        task.onError?.(error);
        task.onComplete?.({ success: false, error });
      }
    }
  }

  // Get current queue status
  getQueueStatus(): { queueLength: number; isProcessing: boolean } {
    return {
      queueLength: this.retryQueue.length,
      isProcessing: this.isProcessing
    };
  }

  // Clear the retry queue
  clearQueue(): void {
    this.retryQueue = [];
    console.log('Retry queue cleared');
  }
}

// Create singleton instance
export const retryService = new RetryService(); 